import { offset, shift } from '@floating-ui/react-dom';
import { CLEAR_COLOR_NAME } from '@typedream/core';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { useOutsideAlerter, useOutsideEvent } from '../../utils/eventAlerter';
import { ColorInput, ColorItem, ColorPicker } from '../ColorPicker';
import { Floating } from '../Floating';
import styles from './SelectColor.module.css';

interface SelectColorProps<T extends Record<string, string | undefined>> {
  disabled?: boolean
  options: T
  value: keyof T & string
  onSelect: (value: keyof T) => void
  enableCustomColor?: boolean
}

export function SelectColor<T extends Record<string, string | undefined>>(
  props: SelectColorProps<T>,
) {
  const {
    disabled, options, value, onSelect, enableCustomColor,
  } = props;

  const isPredefinedColor = Object.keys(options).includes(value);
  const colorValue = isPredefinedColor ? (options[value] as string) : value;

  const [isReplacingColor, setIsReplacingColor] = useState(false);

  const ref = useRef(null);
  useOutsideAlerter(ref, '', () => setIsReplacingColor(false));

  const floatingRef = useRef<Floating>(null);

  const buttonRef = useOutsideEvent<HTMLButtonElement>('mousedown', (event) => {
    if (
      floatingRef.current?.container === event.target
      || floatingRef.current?.container?.contains(event.target as Node)
    ) {
      return;
    }
    setIsReplacingColor(false);
  });

  return (
    <div ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.button}
        onClick={() => setIsReplacingColor((v) => !v)}
        disabled={disabled}
      >
        <span
          className={classNames(styles.colorShowcase, {
            [styles.withCrossLine]: value === CLEAR_COLOR_NAME,
          })}
          style={{
            background: colorValue,
          }}
        />
        {value}
      </button>

      <Floating
        ref={floatingRef}
        show={isReplacingColor}
        reference={buttonRef}
        options={{
          strategy: 'fixed',
          placement: 'left-start',
          middleware: [
            offset({ mainAxis: 32, crossAxis: -24 }),
            shift({ crossAxis: true, padding: { top: 8, bottom: 8 } }),
          ],
        }}
        className={styles.floating}
        autoUpdate
      >
        <ColorPicker value={isPredefinedColor ? value : ''} onChange={onSelect}>
          {Object.entries(options).map(([k, v]) => (
            <ColorItem key={k} colorKey={k} colorValue={v} />
          ))}
        </ColorPicker>

        {enableCustomColor && (
          <ColorInput
            predefinedColors={options}
            value={value}
            onSubmit={onSelect}
          />
        )}
      </Floating>
    </div>
  );
}

SelectColor.defaultProps = {
  disabled: false,
  enableCustomColor: false,
};
